'use client';

import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import {type FC, memo, type ReactElement, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {StorageImageNew} from '@/components/atoms/StorageImage';
import type {Celebrity} from '@/entities/Celebrity/type/celebrities';
import {cn} from '@/libs/utils';
import ShowcaseContextNew from './ShowcaseContextNew';

interface CarouselCelebritiesProps {
  celebrities: Celebrity[];
}

interface CelebrityItemProps {
  celebrity: Celebrity;
  idx: number;
  isActive: boolean;
  onSelect: (idx: number) => void;
  isPriority: boolean;
}

const CelebrityItem: FC<CelebrityItemProps> = memo(
  ({celebrity, idx, isActive, onSelect, isPriority}) => {
    const {first_name, last_name, header_avatar_img} = celebrity;

    const handleClick = useCallback(() => {
      onSelect(idx);
    }, [idx, onSelect]);

    return (
      <div
        role="presentation"
        onClick={handleClick}
        className="flex cursor-pointer flex-col items-center justify-center px-2 first:pl-0 last:pr-0 md:px-[18px]"
      >
        <div
          className={`flex size-[60px] flex-col items-center justify-center rounded-full transition-colors md:size-[140px] p-px md:p-0.5`}
        >
          {header_avatar_img && (
            <div
              className={cn(
                'h-full w-full overflow-hidden rounded-full border border-4 md:border-8',
                isActive ? 'border-[#FFFFFF]' : 'border-transparent',
              )}
            >
              <StorageImageNew
                src={header_avatar_img}
                alt={`${first_name} ${last_name}`}
                className="h-full w-full rounded-full"
                withBlur
                priority={isPriority}
                aspectRatio="1/1"
                sizes="(min-width: 768px) 100px, 60px"
                quality={80}
                resize="cover"
                sources={[
                  {
                    media: '(min-width: 1280px)',
                    width: 80,
                    height: 80,
                    resize: 'cover',
                    dprs: [1, 2, 3],
                  },
                  {
                    media: '(min-width: 768px)',
                    width: 100,
                    height: 100,
                    resize: 'cover',
                    dprs: [1, 2],
                  },
                  {
                    media: '(max-width: 767px)',
                    width: 60,
                    height: 60,
                    resize: 'cover',
                    dprs: [1, 2],
                  },
                ]}
              />
            </div>
          )}
        </div>

        <span className="inline-block max-w-[60px] flex-auto break-all pt-3 text-center font-['Inter'] text-[14px] font-semibold uppercase leading-xm md:leading-[28px] text-white md:max-w-[140px] md:text-[20px]">
          {first_name}
          <br />
          {last_name}
        </span>

        {/* <span
          className={`mt-6 hidden h-[5px] items-center justify-center text-2xs transition-opacity md:inline-flex ${
            isActive ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <Image src="/icons/chevron-right.svg" alt="Chevron" width={24} height={24} className="rotate-[-90deg]" />
        </span> */}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isActive === nextProps.isActive &&
      prevProps.celebrity.id === nextProps.celebrity.id &&
      prevProps.isPriority === nextProps.isPriority
    );
  },
);

CelebrityItem.displayName = 'CelebrityItem';

const createAutoplayPlugin = () =>
  Autoplay({
    delay: 3000,
    stopOnInteraction: false,
    stopOnMouseEnter: false,
  });

export const CarouselCelebritiesNew: FC<CarouselCelebritiesProps> = memo(({celebrities}): ReactElement | null => {
  const {activeIdx, setActiveIdx} = useContext(ShowcaseContextNew);
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const priorityCount = useMemo(() => (isMobile ? 3 : 5), [isMobile]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const checkMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < 1024);
      }, 150);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const autoplayPlugin = useMemo(() => createAutoplayPlugin(), []);

  const [emblaRefMobile, emblaApiMobile] = useEmblaCarousel(
    {
      align: 'center',
      containScroll: 'trimSnaps',
      skipSnaps: false,
      dragFree: false,
      loop: true,
    },
    [autoplayPlugin],
  );

  const onSelect = useCallback(() => {
    if (!emblaApiMobile || !isMobile) return;
    const selectedIndex = emblaApiMobile.selectedScrollSnap();
    setActiveIdx(selectedIndex);
  }, [emblaApiMobile, setActiveIdx, isMobile]);

  useEffect(() => {
    if (!emblaApiMobile || !isMobile) return;
    onSelect();
    emblaApiMobile.on('select', onSelect);
    emblaApiMobile.on('reInit', onSelect);
    return () => {
      emblaApiMobile.off('select', onSelect);
      emblaApiMobile.off('reInit', onSelect);
    };
  }, [emblaApiMobile, onSelect, isMobile]);

  useEffect(() => {
    if (!emblaApiMobile || !isMobile) return;
    const currentIndex = emblaApiMobile.selectedScrollSnap();
    if (currentIndex !== activeIdx) {
      emblaApiMobile.scrollTo(activeIdx);
    }
  }, [activeIdx, emblaApiMobile, isMobile]);

  const handleItemSelect = useCallback(
    (idx: number) => {
      setActiveIdx(idx);
    },
    [setActiveIdx],
  );

  const celebritiesList = useMemo(
    () =>
      celebrities.map((celebrity, idx) => (
        <CelebrityItem
          key={celebrity.id}
          celebrity={celebrity}
          idx={idx}
          isActive={idx === activeIdx}
          onSelect={handleItemSelect}
          isPriority={idx < priorityCount}
        />
      )),
    [celebrities, activeIdx, handleItemSelect, priorityCount],
  );

  const content = <div className="flex items-stretch justify-start lg:justify-center">{celebritiesList}</div>;

  useEffect(() => {
    if (isMobile || celebrities.length === 0 || isPaused) return;

    const interval = setInterval(() => {
      const nextIdx = activeIdx + 1;
      setActiveIdx(nextIdx >= celebrities.length ? 0 : nextIdx);
    }, 3000);

    return () => clearInterval(interval);
  }, [isMobile, celebrities.length, activeIdx, setActiveIdx, isPaused]);

  const handleMouseEnter = useCallback(() => setIsPaused(true), []);
  const handleMouseLeave = useCallback(() => setIsPaused(false), []);

  if (isMobile) {
    return (
      <div className="overflow-hidden" ref={emblaRefMobile}>
        {content}
      </div>
    );
  }

  return (
    <div
      className="overflow-x-auto overflow-y-hidden scroll-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </div>
  );
});

CarouselCelebritiesNew.displayName = 'CarouselCelebritiesNew';
